import type { Booking, Call, Position, RateCard } from './types.js';
import { costFromDayRateHours, costFromHourlyRateHours, type OTCostOutput, type StandardCostOutput } from './ot-cost.js';
import { hoursForCall, dateKey } from './quote.js';
import type { SharedAllocation } from './sharedDay.js';
import {
  costForBookingByDayStandardHourly,
  costForBookingByDayStandardDayRate,
  type CostMode,
  type Holiday,
} from './standard-timekeeping.js';

export interface BudgetPositionSummary {
  positionId: string;
  role: string;
  label?: string;
  budgeted: number;
  // Sum, over active bookings, of each booking's own callIds run through the
  // day-rate/OT math, bucketed by calendar day (OT still resets daily even
  // though a Position itself can span several days).
  actualOfferedCost: number;
  headcount: number;
  quantity: number;
  overBudget: boolean;
  overQuantity: boolean;
  // Sum of cancellation fees marked OWED or PAID on CANCELLED bookings under
  // this position — WAIVED fees are recorded (for the reason/history) but
  // cost nothing, so they're excluded here. Deliberately NOT folded into
  // actualOfferedCost/overBudget: that figure means "committed labor spend
  // for work that will happen," and a cancellation fee is a different kind
  // of money (owed for work that WON'T happen) — mixing them would silently
  // change every existing budget number and quote line (buildLaborLines
  // consumes actualOfferedCost). Surfaced as its own figure in the UI.
  cancellationFeeTotal: number;
}

// Bookings in any of these states represent a real (if not yet finalized)
// commitment against the position's budget. CANCELLED — like DECLINED — is
// deliberately excluded: a cancelled booking no longer represents work that
// will happen, so it contributes $0/0 headcount here (its cancellation fee,
// if any, is tracked separately as cancellationFeeTotal below, not folded
// into this figure).
export const ACTIVE_STATES = new Set(['DRAFT', 'OFFERED', 'ACCEPTED']);

// Cost of ONE booking: its own callIds (a subset of the parent position's),
// bucketed by calendar day (OT resets daily), run through the day-rate/OT
// math at the BOOKING's own rate (the per-person override captured at
// assign time — falls back to the position's rate only if unset, mirroring
// assignPerson's own fallback) — never the position's rate directly, since
// two bookings under the same position can have different negotiated
// rates. A booking with no linked calls yet (call-less/day-scoped) costs
// $0 — the quote's max(budgeted, actual) floor covers that case.
// Same math as costForBooking but keyed by calendar day instead of summed to
// one total — lets a caller (buildLaborLines) build one quote/invoice line
// per position per day, each carrying its own OT/hours detail, rather than
// one line for the whole position's span.
export function costForBookingByDay(
  booking: Booking,
  position: Pick<Position, 'rate' | 'rateType' | 'rateCardId' | 'rateCardRole'>,
  calls: Call[],
  rateCards: RateCard[],
  timeZone = 'UTC',
  holidays?: Holiday[],
  // Default 'estimate': this prices offers, quotes and the staffing budget from
  // the schedule. Timesheet/payroll costing (actuals.ts) passes 'actual'.
  mode: CostMode = 'estimate',
  // Shared-day pricing (Planning/SharedDay.md): a booking that's part of one
  // is priced as part of its person-day, not on its own — its allocated share
  // replaces the per-booking result.
  shared?: SharedAllocation,
): Map<string, OTCostOutput | StandardCostOutput> {
  const sharedShare = shared?.get(booking.id);
  if (sharedShare) return sharedShare;
  const rateCardById = new Map(rateCards.map(rc => [rc.id, rc]));
  const callById = new Map(calls.map(c => [c.id, c]));

  const card = position.rateCardId ? rateCardById.get(position.rateCardId) : undefined;
  const roleRate = card && position.rateCardRole ? card.roles[position.rateCardRole] : undefined;
  const otStartHrs = roleRate?.otStartHrs ?? 10;
  const minHourlyBill = roleRate?.minHourlyBill ?? 0;
  const useDayRate = (booking.rateType ?? position.rateType) !== 'hourly';

  const bookingCalls = booking.callIds.map(id => callById.get(id)).filter((c): c is Call => Boolean(c));

  // Standard Timekeeping (Planning/Timekeeping.md) — opt-in per rate-card
  // role. Everything below this block (the legacy single-band, calendar-day
  // flat-floor calculation) is untouched and still runs byte-for-byte the
  // same for every role that hasn't opted in.
  if (roleRate?.useStandardTimekeeping) {
    const rate = booking.rate?.amount ?? position.rate.amount;
    return useDayRate
      ? costForBookingByDayStandardDayRate(bookingCalls, rate, roleRate, holidays, timeZone, mode)
      : costForBookingByDayStandardHourly(bookingCalls, rate, roleRate, holidays, timeZone);
  }

  const byDay = new Map<string, Call[]>();
  for (const call of bookingCalls) {
    const key = dateKey(call.startTime, timeZone);
    const list = byDay.get(key);
    if (list) list.push(call);
    else byDay.set(key, [call]);
  }

  const rate = booking.rate?.amount ?? position.rate.amount;
  const result = new Map<string, OTCostOutput>();
  for (const [key, dayCalls] of byDay) {
    const actualHours = dayCalls.reduce((sum, c) => sum + hoursForCall(c), 0);
    const billedHours = Math.max(actualHours, minHourlyBill);
    const cost = useDayRate
      ? costFromDayRateHours({ hoursWorked: billedHours, dayRate: rate, hourlyRate: 0, tier: 0, otStartHrs })
      : costFromHourlyRateHours({ hoursWorked: billedHours, dayRate: 0, hourlyRate: rate, tier: 0, otStartHrs });
    result.set(key, cost);
  }
  return result;
}

// Cost of ONE booking: its own callIds (a subset of the parent position's),
// bucketed by calendar day (OT resets daily), run through the day-rate/OT
// math at the BOOKING's own rate (the per-person override captured at
// assign time — falls back to the position's rate only if unset, mirroring
// assignPerson's own fallback) — never the position's rate directly, since
// two bookings under the same position can have different negotiated
// rates. A booking with no linked calls yet (call-less/day-scoped) costs
// $0 — the quote's max(budgeted, actual) floor covers that case.
export function costForBooking(
  booking: Booking,
  position: Pick<Position, 'rate' | 'rateType' | 'rateCardId' | 'rateCardRole'>,
  calls: Call[],
  rateCards: RateCard[],
  timeZone = 'UTC',
  holidays?: Holiday[],
  mode: CostMode = 'estimate',
  shared?: SharedAllocation,
): number {
  const byDay = costForBookingByDay(booking, position, calls, rateCards, timeZone, holidays, mode, shared);
  let total = 0;
  for (const cost of byDay.values()) total += cost.totalCost;
  return Math.round(total * 100) / 100;
}

// One real Position doc per staffing need now (no more dayGroupId grouping/
// "first position" footgun) — this computes, per Position, its budget cap
// vs. what's actually been offered. Cost is computed PER BOOKING (not
// pooled across the whole position) since two bookings under the same
// Position can legitimately cover different call subsets AND different
// negotiated rates (quantity > 1, e.g. two people splitting a multi-call
// slot at different rates) — see costForBooking.
export function summarizeBudget(
  positions: Position[],
  bookingsByPosition: Record<string, Booking[]>,
  calls: Call[],
  rateCards: RateCard[],
  timeZone = 'UTC',
  holidays?: Holiday[],
  shared?: SharedAllocation,
): BudgetPositionSummary[] {
  return positions.map(pos => {
    const budgeted = pos.maxBudget ?? 0;

    let actualOfferedCost = 0;
    let headcount = 0;
    let cancellationFeeTotal = 0;
    for (const booking of bookingsByPosition[pos.id] ?? []) {
      if (booking.state === 'CANCELLED') {
        if (booking.cancellationFee && booking.cancellationFeeStatus !== 'WAIVED') {
          cancellationFeeTotal += booking.cancellationFee.amount;
        }
        continue;
      }
      if (!ACTIVE_STATES.has(booking.state)) continue;
      headcount += 1;
      actualOfferedCost += costForBooking(booking, pos, calls, rateCards, timeZone, holidays, 'estimate', shared);
    }

    return {
      positionId: pos.id,
      role: pos.role,
      label: pos.label,
      budgeted,
      actualOfferedCost: Math.round(actualOfferedCost * 100) / 100,
      headcount,
      quantity: pos.quantity,
      overBudget: budgeted > 0 && actualOfferedCost > budgeted,
      overQuantity: headcount > pos.quantity,
      cancellationFeeTotal: Math.round(cancellationFeeTotal * 100) / 100,
    };
  });
}
